<template>
  <v-navigation-drawer
    v-model="store.sidebarShown"
    app
    location="right"
    :width="effectiveSidebarWidth"
    :temporary="isMobile"
    class="help-drawer"
    mobile-breakpoint="0"
    elevation="8"
    role="dialog"
    aria-modal="true"
    aria-labelledby="helpbar-title"
  >
    <!-- Sticky header -->
    <div class="helpbar-header">
      <div class="helpbar-title-block">
        <div class="helpbar-title-icon">
          <v-icon color="primary" size="24">
            mdi-help-circle-outline
          </v-icon>
        </div>

        <div>
          <h2
            id="helpbar-title"
            class="helpbar-title"
          >
            Hilfe
          </h2>

          <div class="helpbar-subtitle">
            Informationen zur aktuellen Seite
          </div>
        </div>
      </div>

      <v-btn
        icon
        variant="text"
        size="small"
        title="Hilfe schließen"
        aria-label="Hilfe schließen"
        @click="closeHelpbar"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Width controls: desktop only -->
    <div
      v-if="!isMobile"
      class="helpbar-width-controls"
    >
      <div class="width-control-label">
        <v-icon size="18" color="primary">
          mdi-arrow-split-vertical
        </v-icon>

        <span>Breite</span>
      </div>

      <v-slider
        v-model="store.sidebarWidth"
        class="helpbar-width-slider"
        :min="MIN_WIDTH"
        :max="effectiveMaxWidth"
        :step="10"
        hide-details
        density="compact"
        color="primary"
        track-color="surface-variant"
        thumb-label
        aria-label="Breite des Hilfebereichs"
        @end="saveSidebarWidth"
      />
    </div>

    <!-- Help content -->
    <div class="helpbar-content">
      <component
        :is="store.helpComponent || NewMethod"
        class="markdown-body"
      />
    </div>

    <!-- Bottom close action on mobile -->
    <div
      v-if="isMobile"
      class="helpbar-mobile-footer"
    >
      <v-btn
        block
        color="primary"
        variant="tonal"
        @click="closeHelpbar"
      >
        <v-icon start>mdi-close</v-icon>
        Hilfe schließen
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useProjectStore } from '@/stores/projectStore'
import '@/assets/StyleMethodHelp.css'
import NewMethod from '@/components/help/NewMethod.vue'

const store = useProjectStore()

const MIN_WIDTH = 280
const MAX_WIDTH = 960
const MOBILE_BREAKPOINT = 700

const viewportWidth = ref(
  typeof window !== 'undefined'
    ? window.innerWidth
    : 1200
)

const isMobile = computed(() => {
  return viewportWidth.value <= MOBILE_BREAKPOINT
})

/*
 * Prevent the drawer from becoming wider than the
 * available viewport on medium-sized screens.
 */
const effectiveMaxWidth = computed(() => {
  return Math.max(
    MIN_WIDTH,
    Math.min(MAX_WIDTH, viewportWidth.value - 32)
  )
})

/*
 * Mobile drawer uses the complete viewport width.
 * Desktop uses the stored adjustable width.
 */
const effectiveSidebarWidth = computed(() => {
  if (isMobile.value) {
    return viewportWidth.value
  }

  return Math.min(
    store.sidebarWidth,
    effectiveMaxWidth.value
  )
})

function updateViewportWidth() {
  viewportWidth.value = window.innerWidth
}

function closeHelpbar() {
  if (typeof store.hideSidebar === 'function') {
    store.hideSidebar()
  } else {
    store.sidebarShown = false
  }
}

function saveSidebarWidth() {
  if (typeof store.saveSidebarWidth === 'function') {
    store.saveSidebarWidth()
  }
}

/*
 * Keyboard behavior:
 * - Escape closes the helpbar
 * - Alt + Left makes it wider
 * - Alt + Right makes it narrower
 */
function onKeydown(event) {
  if (!store.sidebarShown) return

  if (event.key === 'Escape') {
    closeHelpbar()
    return
  }

  if (isMobile.value || !event.altKey) return

  const step = 20

  if (event.key === 'ArrowLeft') {
    event.preventDefault()

    store.sidebarWidth = Math.min(
      store.sidebarWidth + step,
      effectiveMaxWidth.value
    )
  }

  if (event.key === 'ArrowRight') {
    event.preventDefault()

    store.sidebarWidth = Math.max(
      store.sidebarWidth - step,
      MIN_WIDTH
    )
  }
}

/*
 * Protect against invalid values coming from storage
 * or external store changes.
 */
watch(
  () => store.sidebarWidth,
  width => {
    const numericWidth = Number(width)

    const safeWidth = Number.isFinite(numericWidth)
      ? numericWidth
      : 420

    const clampedWidth = Math.min(
      Math.max(safeWidth, MIN_WIDTH),
      effectiveMaxWidth.value
    )

    if (clampedWidth !== width) {
      store.sidebarWidth = clampedWidth
    }
  },
  {
    immediate: true,
  }
)

/*
 * Re-clamp the stored width after viewport changes.
 */
watch(effectiveMaxWidth, maxWidth => {
  if (
    !isMobile.value &&
    store.sidebarWidth > maxWidth
  ) {
    store.sidebarWidth = maxWidth
  }
})

onMounted(() => {
  store.loadSidebarWidth?.()

  window.addEventListener(
    'resize',
    updateViewportWidth,
    { passive: true }
  )

  window.addEventListener(
    'keydown',
    onKeydown
  )
})

onBeforeUnmount(() => {
  window.removeEventListener(
    'resize',
    updateViewportWidth
  )

  window.removeEventListener(
    'keydown',
    onKeydown
  )
})
</script>

<style scoped lang="scss">
.help-drawer {
  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

/* ========================================
   Sticky header
======================================== */

.helpbar-header {
  position: sticky;
  top: 0;
  z-index: 10;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  min-height: 76px;
  padding: 0.85rem 1rem;

  border-bottom: 1px solid
    rgba(var(--v-theme-on-surface), 0.08);

  background:
    linear-gradient(
      180deg,
      rgba(var(--v-theme-primary), 0.08),
      rgba(var(--v-theme-primary), 0.035)
    ),
    rgb(var(--v-theme-surface));

  backdrop-filter: blur(12px);
}

.helpbar-title-block {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.helpbar-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 42px;
  height: 42px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.1);
}

.helpbar-title {
  margin: 0;

  color: rgb(var(--v-theme-on-surface));

  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
}

.helpbar-subtitle {
  margin-top: 0.15rem;

  color: rgba(var(--v-theme-on-surface), 0.62);

  font-size: 0.78rem;
  line-height: 1.3;
}

/* ========================================
   Width control
======================================== */

.helpbar-width-controls {
  position: sticky;
  top: 76px;
  z-index: 9;

  padding: 0.7rem 1rem 0.8rem;

  border-bottom: 1px solid
    rgba(var(--v-theme-on-surface), 0.07);

  background-color: rgba(
    var(--v-theme-surface),
    0.96
  );

  backdrop-filter: blur(10px);
}

.width-control-label {
  display: flex;
  align-items: center;
  gap: 0.45rem;

  margin-bottom: 0.25rem;

  color: rgba(var(--v-theme-on-surface), 0.75);

  font-size: 0.78rem;
  font-weight: 600;
}

.helpbar-width-slider {
  :deep(.v-slider-thumb__label) {
    min-width: max-content !important;
    height: auto !important;
    padding: 0.5rem 0.7rem !important;

    border: 1px solid
      rgba(var(--v-theme-on-surface), 0.14) !important;
    border-radius: 10px !important;

    color: rgb(var(--v-theme-on-surface)) !important;
    background-color: rgb(var(--v-theme-surface)) !important;

    box-shadow:
      0 6px 18px rgba(0, 0, 0, 0.18) !important;

    font-size: 0.82rem !important;
    font-weight: 600 !important;
    line-height: 1.3 !important;
    white-space: nowrap !important;
  }

  :deep(.v-slider-thumb__label::before) {
    color: rgb(var(--v-theme-surface)) !important;
  }
}


/* ========================================
   Content
======================================== */

.helpbar-content {
  padding: 0.5rem;
}

.markdown-body {
  box-sizing: border-box;

  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: clamp(1rem, 2.5vw, 1.5rem);

  overflow-wrap: anywhere;
  word-break: break-word;

  color: rgb(var(--v-theme-on-surface));
}

/* ========================================
   Markdown typography
======================================== */

:deep(.markdown-body h1),
:deep(.markdown-body h2),
:deep(.markdown-body h3),
:deep(.markdown-body h4) {
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.25;
}

:deep(.markdown-body h1) {
  margin: 0 0 1rem;
  font-size: 1.55rem;
}

:deep(.markdown-body h2) {
  margin: 1.5rem 0 0.7rem;
  font-size: 1.25rem;
}

:deep(.markdown-body h3) {
  margin: 1.2rem 0 0.6rem;
  font-size: 1.05rem;
}

:deep(.markdown-body h4) {
  margin: 1rem 0 0.5rem;
  font-size: 0.95rem;
}

:deep(.markdown-body p),
:deep(.markdown-body ul),
:deep(.markdown-body ol),
:deep(.markdown-body blockquote) {
  margin: 0 0 0.85rem;
  line-height: 1.6;
}

:deep(.markdown-body ul),
:deep(.markdown-body ol) {
  padding-inline-start: 1.35rem;
}

:deep(.markdown-body li) {
  margin-bottom: 0.3rem;
}

/* ========================================
   Links
======================================== */

:deep(.markdown-body a) {
  color: rgb(var(--v-theme-primary));

  font-weight: 600;
  text-decoration: none;
  text-underline-offset: 3px;
}

:deep(.markdown-body a:hover) {
  text-decoration: underline;
}

/* ========================================
   Blockquotes and callouts
======================================== */

:deep(.markdown-body blockquote) {
  padding: 0.75rem 0.9rem;

  border-left: 3px solid
    rgb(var(--v-theme-primary));
  border-radius: 0 12px 12px 0;

  color: rgba(var(--v-theme-on-surface), 0.78);
  background-color: rgba(var(--v-theme-primary), 0.055);
}

/* ========================================
   Images
======================================== */

:deep(.markdown-body img) {
  display: block;

  max-width: 100%;
  height: auto;
  margin: 0.75rem auto;

  border: 1px solid
    rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 14px;
}

/* ========================================
   Inline code and code blocks
======================================== */

:deep(.markdown-body code) {
  padding: 0.15rem 0.35rem;

  border: 1px solid
    rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 6px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgba(
    var(--v-theme-on-surface),
    0.07
  );

  font-family:
    ui-monospace,
    SFMono-Regular,
    Menlo,
    Monaco,
    Consolas,
    monospace;

  font-size: 0.9em;
}

:deep(.markdown-body pre) {
  max-width: 100%;
  padding: 0.9rem;

  overflow: auto;

  border: 1px solid
    rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 12px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgba(
    var(--v-theme-on-surface),
    0.055
  );
}

:deep(.markdown-body pre code) {
  padding: 0;

  border: 0;
  background: transparent;
}

/* ========================================
   Tables
======================================== */

:deep(.markdown-body table) {
  display: block;

  width: 100%;
  max-width: 100%;

  overflow-x: auto;

  border-spacing: 0;
  border-collapse: collapse;

  scrollbar-width: thin;
}

:deep(.markdown-body th),
:deep(.markdown-body td) {
  padding: 0.55rem 0.75rem;

  border-bottom: 1px solid
    rgba(var(--v-theme-on-surface), 0.1);

  text-align: left;
  white-space: nowrap;
}

:deep(.markdown-body th) {
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.05);

  font-weight: 700;
}

/* ========================================
   Mobile footer
======================================== */

.helpbar-mobile-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;

  padding: 0.75rem 1rem;

  border-top: 1px solid
    rgba(var(--v-theme-on-surface), 0.08);

  background-color: rgba(
    var(--v-theme-surface),
    0.96
  );

  backdrop-filter: blur(10px);
}

/* ========================================
   Responsive
======================================== */

@media (max-width: 700px) {
  .helpbar-header {
    min-height: 68px;
    padding: 0.75rem;
  }

  .helpbar-title-icon {
    width: 38px;
    height: 38px;
  }

  .helpbar-content {
    padding: 0.25rem;
  }

  .markdown-body {
    max-width: 100%;
    padding: 0.9rem;
  }
}
</style>